import { SupabaseClient } from '@supabase/supabase-js';
import { ConflictError } from '@repo/errors';
import { supabase as supabaseClient } from '../client/browser';
import { v4 as uuidv4 } from 'uuid';

// Helper function to convert Date to YYYY-MM-DD string
function toDateString(date: Date | string | undefined): string | undefined {
  if (!date) return undefined;

  const d = typeof date === 'string' ? new Date(date) : date;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Mirrors the Postgres `TherapyType` enum. Widen this alias when a
 * new iCBT program ships (or any other therapy modality is added).
 * Keeping it named + shared avoids the 4-way copy-paste that let a
 * "!= INDIVIDUAL" check accidentally treat every non-individual
 * modality as group therapy — see `isGroupTherapyType` below.
 */
export type TherapyType =
  | 'INDIVIDUAL'
  | 'COUPLES'
  | 'FAMILY'
  | 'GROUP'
  | 'ICBT_UNSPIRAL'
  | 'ICBT_UPLIFT';

/**
 * Which therapy types actually run as group sessions (multi-attendee,
 * needs a `sessionGroupId`). Explicit list beats "everything except
 * INDIVIDUAL", which would silently rope in the ICBT variants.
 */
export function isGroupTherapyType(t: string | null | undefined): boolean {
  return t === 'GROUP' || t === 'COUPLES' || t === 'FAMILY';
}

export interface EpisodeData {
  id?: string;
  healthRecordId: string;
  userId: string;
  episodeNumber?: number;
  therapyType: TherapyType;
  therapistId: string;
  groupId?: string;
  matchingQuizId?: string;
  startDate?: string;
  endDate?: string;
  status: 'INTAKE' | 'ACTIVE' | 'HOLD' | 'DISCHARGE_PLANNING' | 'COMPLETED' | 'WITHDRAWN' | 'TRANSFERRED';
  sessionRate: number;
  tier: string;
  timezone?: string;
  freeSessionsAllocated?: number;
  promoCode?: string;
  partnershipId?: string;
  partnershipSessions?: number;
  // Computed fields (not stored in DB, calculated on-demand)
  // These are not stored but can be fetched via helper methods
}

export interface CreateEpisodeParams {
  healthRecordId: string;
  userId: string;
  therapyType?: TherapyType;
  therapistId: string;
  matchingQuizId?: string;
  startDate?: string;
  sessionRate?: number;
  tier?: string;
  timezone?: string;
  promoCode?: string;
  freeSessionsAllocated?: number;
  partnershipId?: string;
  partnershipSessions?: number;
  accessCode?: string;
  groupId?: string;
  // MHAP employer coverage
  employerOrgId?: string;
  employerMemberId?: string;
}

/**
 * Episode service for managing therapy episodes
 */
export const episodeService = {
  /**
   * Get episode by ID
   */
  async getById(episodeId: string, client?: SupabaseClient): Promise<EpisodeData | null> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('episodes')
      .select('*')
      .eq('id', episodeId)
      .single();
    
    if (error) {
      console.error('Error fetching episode:', error);
      return null;
    }
    
    return data;
  },

  /**
   * Get episode by ID with user details
   */
  async getByIdWithDetails(episodeId: string, client?: SupabaseClient): Promise<any | null> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('episodes')
      .select(`
        *,
        user:users!episodes_userId_fkey (
          id,
          firstName,
          lastName,
          preferredName,
          gender,
          pronoun,
          email,
          phone,
          emergencyName,
          emergencyPhone,
          birthday,
          unit,
          street,
          city,
          province,
          country,
          postalCode,
          stripeCustomerId,
          referralSource
        ),
        therapist:pros!episodes_therapistId_fkey (
          id,
          firstName,
          lastName,
          email
        ),
        employerOrg:employer_organizations!employerOrgId (
          id,
          name,
          kind
        )
      `)
      .eq('id', episodeId)
      .single();
    
    if (error) {
      console.error('Error fetching episode with details:', error);
      return null;
    }
    
    return data;
  },

  /**
   * Get all episodes for a user
   */
  async getByUserId(userId: string, client?: SupabaseClient): Promise<EpisodeData[]> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('episodes')
      .select('*')
      .eq('userId', userId)
      .order('episodeNumber', { ascending: false });
    
    if (error) {
      console.error('Error fetching episodes:', error);
      return [];
    }
    
    return data || [];
  },

  /**
   * Get active episodes for a user (with therapist info)
   */
  async getActiveByUserId(userId: string, client?: SupabaseClient): Promise<any[]> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('episodes')
      .select(`
        *,
        therapist:pros!therapistId (
          id,
          firstName,
          lastName,
          profileImage
        )
      `)
      .eq('userId', userId)
      .in('status', ['INTAKE', 'ACTIVE'])
      .order('startDate', { ascending: false });
    
    if (error) {
      console.error('Error fetching active episodes:', error);
      return [];
    }
    
    return data || [];
  },

  /**
   * Check for existing active episode by health record and therapy type
   */
  async checkExistingEpisode(
    healthRecordId: string,
    therapyType: TherapyType,
    client?: SupabaseClient
  ): Promise<{ exists: boolean; episode?: EpisodeData; canProceed: boolean; message?: string }> {
    const supabase = client || supabaseClient;
    
    // Get active episodes for this health record and therapy type
    const { data, error } = await supabase
      .from('episodes')
      .select('*')
      .eq('healthRecordId', healthRecordId)
      .eq('therapyType', therapyType)
      .in('status', ['INTAKE', 'ACTIVE']);
    
    if (error) {
      console.error('Error checking existing episode:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      return { exists: false, canProceed: true };
    }
    
    // Episode exists
    const existingEpisode = data[0];
    return {
      exists: true,
      episode: existingEpisode,
      canProceed: false,
      message: `You already have an active ${therapyType.toLowerCase()} therapy plan. Please complete or cancel your current therapy before starting with a new therapist.`
    };
  },

  /**
   * Validate episode creation with therapist check
   */
  async validateEpisodeCreation(
    healthRecordId: string,
    therapyType: TherapyType,
    therapistId: string,
    client?: SupabaseClient
  ): Promise<{ canCreate: boolean; existingEpisodeId?: string; message?: string }> {
    const supabase = client || supabaseClient;

    // For couples/group therapy, allow multiple active episodes (different relationships)
    // For individual therapy, restrict to one active episode per therapist
    if (therapyType === 'COUPLES' || therapyType === 'GROUP') {
      // For couples/group therapy, always allow creating new episodes
      // Each episode represents a different relationship/group
      console.log('[Episode] Couples/group therapy - allowing new episode creation');
      return { canCreate: true };
    }

    // For individual therapy, check for existing episodes
    const check = await this.checkExistingEpisode(healthRecordId, therapyType, supabase);

    if (!check.exists) {
      // No existing episode, can create new one
      return { canCreate: true };
    }

    // Episode exists - check if it's with the same therapist
    if (check.episode?.therapistId === therapistId) {
      // Same therapist, can proceed with booking but don't create new episode
      return {
        canCreate: false,
        existingEpisodeId: check.episode.id,
        message: 'Continuing with your current therapist'
      };
    }

    // Different therapist for individual therapy - cannot proceed
    return {
      canCreate: false,
      message: `You're currently working with another therapist for ${therapyType.toLowerCase()} therapy. To switch therapists, please end your current therapy relationship first.`
    };
  },

  /**
   * Get the next episode number for a health record
   */
  async getNextEpisodeNumber(healthRecordId: string, client?: SupabaseClient): Promise<number> {
    const supabase = client || supabaseClient;
    
    const { data } = await supabase
      .from('episodes')
      .select('episodeNumber')
      .eq('healthRecordId', healthRecordId)
      .order('episodeNumber', { ascending: false })
      .limit(1)
      .single();
    
    return data ? data.episodeNumber + 1 : 1;
  },

  /**
   * Create a new episode
   */
  async create(params: CreateEpisodeParams, client?: SupabaseClient): Promise<string> {
    const supabase = client || supabaseClient;
    
    // Get the next episode number
    const nextEpisodeNumber = await this.getNextEpisodeNumber(params.healthRecordId, supabase);
    
    const therapyType = params.therapyType || 'INDIVIDUAL';
    let sessionRate = params.sessionRate || 0;
    const tier = params.tier || 'resident';
    
    // Calculate free sessions and apply promo code if provided (but not if partnership)
    let freeSessionsAllocated = 0;
    let appliedPromoCode = params.promoCode;

    console.log('[Episode] Create called with:', {
      promoCode: params.promoCode,
      partnershipId: params.partnershipId
    });

    // If partnership is present, ignore promo code
    if (params.partnershipId) {
      console.log('[Episode] Partnership present, ignoring promo code');
      appliedPromoCode = undefined;
    } else if (params.promoCode) {
      // Fetch promo details
      const { data: promo, error: promoError } = await supabase
        .from('promos')
        .select('*')
        .eq('code', params.promoCode)
        .single();
      
      console.log('[Episode] Promo lookup result:', { 
        promoCode: params.promoCode,
        found: !!promo,
        error: promoError?.message,
        promoData: promo ? {
          discountType: promo.discountType,
          discountValue: promo.discountValue,
          validFrom: promo.validFrom,
          validUntil: promo.validUntil
        } : null
      });
      
      if (!promoError && promo) {
        // Check if promo is valid
        const now = new Date();
        const validFrom = new Date(promo.validFrom);
        const validUntil = promo.validUntil ? new Date(promo.validUntil) : null;
        
        // Promo is valid if current time is after validFrom and before validUntil (or no validUntil)
        if (now >= validFrom && (!validUntil || now <= validUntil)) {
          console.log('[Episode] Applying promo code:', params.promoCode, 'Type:', promo.discountType);
          
          // Apply discount based on type
          switch (promo.discountType) {
            case 'FREE_SESSIONS':
              freeSessionsAllocated = promo.discountValue || 1;
              console.log('[Episode] Allocated', freeSessionsAllocated, 'free sessions');
              break;
            
            case 'PERCENTAGE':
              // Apply percentage discount to session rate
              const percentageOff = promo.discountValue || 0;
              const discountAmount = sessionRate * (percentageOff / 100);
              sessionRate = Math.max(0, sessionRate - discountAmount);
              console.log('[Episode] Applied', percentageOff, '% discount, new rate:', sessionRate);
              break;
            
            case 'FIXED_AMOUNT':
              // Apply fixed amount discount to session rate
              const fixedDiscount = promo.discountValue || 0;
              sessionRate = Math.max(0, sessionRate - fixedDiscount);
              console.log('[Episode] Applied $', fixedDiscount, 'discount, new rate:', sessionRate);
              break;
            
            default:
              console.log('[Episode] Unknown promo discount type:', promo.discountType);
          }
        } else {
          console.log('[Episode] Promo code expired or not yet valid:', params.promoCode);
          appliedPromoCode = undefined; // Don't store invalid promo
        }
      } else {
        console.log('[Episode] Promo code not found:', params.promoCode);
        appliedPromoCode = undefined; // Don't store invalid promo
      }
    }
    
    console.log('[Episode] Creating episode with:', {
      promoCode: appliedPromoCode,
      freeSessionsAllocated,
      sessionRate,
      tier,
      partnershipId: params.partnershipId,
      partnershipSessions: params.partnershipSessions,
      accessCode: params.accessCode
    });

    const { data, error } = await supabase
      .from('episodes')
      .insert({
        id: uuidv4(),
        healthRecordId: params.healthRecordId,
        userId: params.userId,
        episodeNumber: nextEpisodeNumber,
        therapyType: therapyType,
        therapistId: params.therapistId,
        matchingQuizId: params.matchingQuizId,
        startDate: toDateString(params.startDate || new Date()),  // Date-only field
        status: 'INTAKE',
        sessionRate: sessionRate,  // Rate after promo discount applied (or partnership rate)
        tier: tier,
        timezone: params.timezone,
        freeSessionsAllocated: freeSessionsAllocated,
        promoCode: appliedPromoCode,  // Only store valid promo codes
        partnershipId: params.partnershipId,
        partnershipSessions: params.partnershipSessions, // Use the actual value from partnership
        partnershipAccessCode: params.accessCode, // Store the access code used
        groupId: params.groupId,
        // MHAP employer coverage
        employerOrgId: params.employerOrgId,
        employerMemberId: params.employerMemberId,
        createdAt: new Date(),
        updatedAt: new Date()
      })
      .select('id')
      .single();
    
    if (error) {
      console.error('Error creating episode:', error);
      throw error;
    }
    
    return data.id;
  },

  /**
   * Update episode status
   */
  async updateStatus(
    episodeId: string,
    status: EpisodeData['status'],
    client?: SupabaseClient
  ): Promise<void> {
    const supabase = client || supabaseClient;
    
    // First, get the current episode details
    const { data: currentEpisode, error: fetchError } = await supabase
      .from('episodes')
      .select('*, therapist:therapistId(id)')
      .eq('id', episodeId)
      .single();
    
    if (fetchError || !currentEpisode) {
      console.error('Error fetching episode:', fetchError);
      throw new Error('File not found');
    }
    
    // Check if trying to reactivate from a terminal status
    const terminalStatuses = ['COMPLETED', 'WITHDRAWN', 'TRANSFERRED'];
    const activeStatuses = ['ACTIVE', 'HOLD', 'INTAKE', 'DISCHARGE_PLANNING'];
    
    if (terminalStatuses.includes(currentEpisode.status) && activeStatuses.includes(status)) {
      // Check if the client has any other active episodes of the same therapy type with a different therapist
      const { data: activeEpisodes, error: checkError } = await supabase
        .from('episodes')
        .select('id, therapistId, createdAt, therapist:pros!therapistId(firstName, lastName)')
        .eq('userId', currentEpisode.userId)
        .eq('therapyType', currentEpisode.therapyType)
        .in('status', activeStatuses)
        .neq('id', episodeId);
      
      if (checkError) {
        console.error('Error checking for active episodes:', checkError);
        throw new Error('Failed to validate file status change');
      }
      
      // Check if any of the active episodes are with a different therapist
      const activeWithDifferentTherapist = activeEpisodes?.find(
        episode => episode.therapistId !== currentEpisode.therapistId
      );
      
      if (activeWithDifferentTherapist) {
        // Get session count for the active episode with another therapist
        const { count: sessionCount } = await supabase
          .from('sessions')
          .select('*', { count: 'exact', head: true })
          .eq('episodeId', activeWithDifferentTherapist.id)
          .in('status', ['SCHEDULED', 'CONFIRMED', 'COMPLETED']);
        
        const otherTherapist = activeWithDifferentTherapist.therapist as any;
        const therapistName = otherTherapist
          ? `${otherTherapist.firstName || ''} ${otherTherapist.lastName || ''}`.trim() || 'another therapist'
          : 'another therapist';
        
        // Format the start date
        const startDate = new Date(activeWithDifferentTherapist.createdAt);
        const formattedDate = startDate.toLocaleDateString('en-US', { 
          month: 'long', 
          day: 'numeric', 
          year: 'numeric' 
        });
        
        // Message intentionally omits the internal file ID and start date.
        // Therapist name is kept because the calling therapist needs it to
        // advise the client. If this path ever gets exercised on behalf of
        // a different user (currently same-viewer only), revisit.
        throw new ConflictError(
          `Cannot reactivate this ${currentEpisode.therapyType.toLowerCase()} therapy file. ` +
          `The client already has an active ${currentEpisode.therapyType.toLowerCase()} therapy file with ${therapistName}. ` +
          `\n\nThe client will need to close their file with ${therapistName} before they can restart therapy with you. ` +
          `Clients can only work with one therapist at a time for each therapy type. ` +
          `Please speak with your client about their current therapy arrangements.`
        );
      }
    }
    
    const updateData: any = {
      status,
      updatedAt: new Date()
    };
    
    // If completing, withdrawing, or transferring, set end date
    if (status === 'COMPLETED' || status === 'WITHDRAWN' || status === 'TRANSFERRED') {
      updateData.endDate = toDateString(new Date());
    } else if (activeStatuses.includes(status)) {
      // If reactivating or changing to active/ongoing status, clear the end date
      updateData.endDate = null;
    }
    
    const { error } = await supabase
      .from('episodes')
      .update(updateData)
      .eq('id', episodeId);
    
    if (error) {
      console.error('Error updating episode status:', error);
      throw error;
    }
  },

  // Note: updateDates method removed as these fields are now computed on-demand

  /**
   * Check if free sessions are available (computed on-demand)
   */
  async canUseFreeSession(episodeId: string, client?: SupabaseClient): Promise<boolean> {
    const supabase = client || supabaseClient;
    
    // Get allocated free sessions
    const { data: episode, error: fetchError } = await supabase
      .from('episodes')
      .select('freeSessionsAllocated')
      .eq('id', episodeId)
      .single();
    
    if (fetchError || !episode) {
      console.error('Error fetching episode for free session check:', fetchError);
      return false;
    }
    
    // Get count of used free sessions (default is 'episode' so no need to specify)
    const usedCount = await this.getFreeSessionsUsed(episodeId, supabase);
    
    return usedCount < episode.freeSessionsAllocated;
  },

  /**
   * Check if partnership sessions are available (computed on-demand)
   */
  async canUsePartnershipSession(episodeId: string, client?: SupabaseClient): Promise<boolean> {
    const supabase = client || supabaseClient;
    
    // Get allocated partnership sessions
    const { data: episode, error: fetchError } = await supabase
      .from('episodes')
      .select('partnershipSessions')
      .eq('id', episodeId)
      .single();
    
    if (fetchError || !episode || !episode.partnershipSessions) {
      console.error('Error fetching episode for partnership session check:', fetchError);
      return false;
    }
    
    // Get count of used partnership sessions
    const usedCount = await this.getPartnershipSessionsUsed(episodeId, supabase);
    
    return usedCount < episode.partnershipSessions;
  },


  /**
   * Terminate an episode (client-initiated)
   * This will:
   * 1. Update the episode status to WITHDRAWN
   * 2. Cancel all future sessions for this episode
   * 3. Send email notifications to client and therapist
   */
  async terminateEpisode(
    episodeId: string,
    reason?: string,
    client?: SupabaseClient
  ): Promise<void> {
    const supabase = client || supabaseClient;

    // First, get the episode with client and therapist details
    const { data: episode, error: episodeError } = await supabase
      .from('episodes')
      .select(`
        id,
        userId,
        therapistId,
        groupId,
        activeCareRequestId,
        user:users!episodes_userId_fkey (
          id,
          firstName,
          lastName,
          preferredName,
          email,
          timezone
        ),
        therapist:pros!episodes_therapistId_fkey (
          id,
          firstName,
          lastName,
          email,
          timezone
        )
      `)
      .eq('id', episodeId)
      .single();

    if (episodeError || !episode) {
      console.error('Error fetching episode for termination:', episodeError);
      throw new Error('Episode not found');
    }

    // Get all future sessions for this episode (SCHEDULED or CONFIRMED)
    const { data: futureSessions, error: sessionsError } = await supabase
      .from('sessions')
      .select('id, start, duration, status')
      .eq('episodeId', episodeId)
      .in('status', ['SCHEDULED', 'CONFIRMED'])
      .gte('start', new Date().toISOString());

    if (sessionsError) {
      console.error('Error fetching future sessions:', sessionsError);
      // Don't throw - continue with termination even if we can't fetch sessions
    }

    // Cancel all future sessions
    const cancelledSessions: Array<{ start: string; duration: number }> = [];
    if (futureSessions && futureSessions.length > 0) {
      console.log(`[Episode] Cancelling ${futureSessions.length} future sessions for episode ${episodeId}`);

      for (const session of futureSessions) {
        try {
          const { error: cancelError } = await supabase
            .from('sessions')
            .update({
              status: 'CANCELLED',
              cancelledAt: new Date().toISOString(),
              cancelledBy: 'CLIENT',
              cancellationReason: 'Client withdrew from therapy',
              paymentStatus: 'CANCELLED',
              updatedAt: new Date()
            })
            .eq('id', session.id);

          if (cancelError) {
            console.error(`Error cancelling session ${session.id}:`, cancelError);
          } else {
            cancelledSessions.push({
              start: session.start,
              duration: session.duration || 50
            });
          }
        } catch (err) {
          console.error(`Error cancelling session ${session.id}:`, err);
        }
      }
    }

    // Update the episode status
    const { error } = await supabase
      .from('episodes')
      .update({
        status: 'WITHDRAWN',
        endDate: toDateString(new Date()),
        terminationReason: reason || 'Client initiated termination',
        updatedAt: new Date()
      })
      .eq('id', episodeId);

    if (error) {
      console.error('Error terminating episode:', error);
      throw error;
    }

    // CARE program — if this episode was on a reduced rate, end the CARE
    // (which reverts rates on the episode and any future sessions). For
    // couples/group episodes, only end CARE when no other member episode in
    // the same group is still open — partial closure shouldn't pull the rate
    // from the remaining member(s).
    const careId = (episode as any).activeCareRequestId as string | null
    const groupId = (episode as any).groupId as string | null
    if (careId) {
      try {
        let shouldCancel = true
        if (groupId) {
          const { data: openMembers } = await supabase
            .from('episodes')
            .select('id')
            .eq('groupId', groupId)
            .neq('id', episodeId)
            .not('status', 'in', '(COMPLETED,WITHDRAWN,TRANSFERRED)')
            .limit(1)
          if (openMembers && openMembers.length > 0) {
            shouldCancel = false
          }
        }
        if (shouldCancel) {
          const { cancelActive } = await import('./care')
          await cancelActive(careId, { by: 'system' })
        }
      } catch (careErr) {
        console.error('[Episode] CARE auto-cancel on termination failed:', careErr)
      }
    }

    // Log completion
    // Email notifications are handled by the caller (via API route) to avoid
    // importing server-side modules (like 'fs' from @repo/email) in client-side code
    console.log(`[Episode] Episode ${episodeId} terminated. Cancelled ${cancelledSessions.length} future sessions.`);
  },

  /**
   * Get active episode with therapist info for a specific therapy type
   */
  async getActiveEpisodeWithTherapist(
    userId: string,
    therapyType: TherapyType,
    client?: SupabaseClient
  ): Promise<{episode: EpisodeData; therapist: any} | null> {
    const supabase = client || supabaseClient;
    
    // Get active episode for this user and therapy type
    const { data: episode, error: episodeError } = await supabase
      .from('episodes')
      .select('*')
      .eq('userId', userId)
      .eq('therapyType', therapyType)
      .in('status', ['INTAKE', 'ACTIVE'])
      .single();
    
    if (episodeError || !episode) {
      return null;
    }
    
    // Get therapist info
    const { data: therapist, error: therapistError } = await supabase
      .from('pros')
      .select('id, firstName, lastName, displayName, profileImage')
      .eq('id', episode.therapistId)
      .single();
    
    if (therapistError || !therapist) {
      return null;
    }
    
    return { episode, therapist };
  },

  /**
   * Get all active episodes for a therapist with user details
   */
  async getByTherapistId(therapistId: string, client?: SupabaseClient): Promise<any[]> {
    const supabase = client || supabaseClient;

    const { data, error } = await supabase
      .from('episodes')
      .select(`
        *,
        user:users (
          id,
          firstName,
          lastName,
          preferredName,
          email,
          phone
        )
      `)
      .eq('therapistId', therapistId)
      .in('status', ['ACTIVE', 'INTAKE', 'DISCHARGE_PLANNING'])
      .order('startDate', { ascending: false });
    
    if (error) {
      console.error('Error fetching episodes by therapist:', error);
      return [];
    }
    
    return data || [];
  },

  /**
   * Delete an episode and all associated sessions, progress notes, and assessments
   * WARNING: This is a destructive operation that cannot be undone
   * Should only be used by admin users
   */
  async deleteEpisodeWithSessions(episodeId: string, client?: SupabaseClient): Promise<{ success: boolean; deletedSessions: number; deletedNotes: number; deletedAssessments: number; deletedWalletTransactions: number; error?: string }> {
    const supabase = client || supabaseClient;

    try {
      // Get all sessions, progress notes, and assessments associated with this episode
      const [sessionsResult, notesResult, assessmentsResult] = await Promise.all([
        supabase.from('sessions').select('id').eq('episodeId', episodeId),
        supabase.from('progress_notes').select('id').eq('episodeId', episodeId),
        supabase.from('assessments').select('id').eq('episodeId', episodeId)
      ]);

      if (sessionsResult.error) {
        console.error('Error fetching sessions for episode:', sessionsResult.error);
        return { success: false, deletedSessions: 0, deletedNotes: 0, deletedAssessments: 0, deletedWalletTransactions: 0, error: sessionsResult.error.message };
      }

      const sessions = sessionsResult.data || [];
      const notes = notesResult.data || [];
      const assessments = assessmentsResult.data || [];

      const sessionCount = sessions.length;
      const notesCount = notes.length;
      const assessmentsCount = assessments.length;
      let walletTransactionsCount = 0;

      // Delete wallet transactions BEFORE sessions (due to foreign key constraint)
      if (sessions.length > 0) {
        const sessionIds = sessions.map(s => s.id);

        // First, get wallet transactions for these sessions
        const { data: walletTransactions, error: walletTxError } = await supabase
          .from('session_wallet_transactions')
          .select('id')
          .in('sessionId', sessionIds);

        if (walletTxError) {
          console.error('Error fetching wallet transactions:', walletTxError);
          // Continue anyway - wallet transactions might not exist
        }

        walletTransactionsCount = walletTransactions?.length || 0;

        // Delete wallet transactions if any exist
        if (walletTransactionsCount > 0) {
          const walletTxIds = walletTransactions!.map(tx => tx.id);
          const { error: deleteWalletTxError } = await supabase
            .from('session_wallet_transactions')
            .delete()
            .in('id', walletTxIds);

          if (deleteWalletTxError) {
            console.error('Error deleting wallet transactions:', deleteWalletTxError);
            return { success: false, deletedSessions: 0, deletedNotes: 0, deletedAssessments: 0, deletedWalletTransactions: 0, error: deleteWalletTxError.message };
          }
        }
      }

      // Delete all sessions
      if (sessions.length > 0) {
        const sessionIds = sessions.map(s => s.id);
        const { error: deleteSessionsError } = await supabase
          .from('sessions')
          .delete()
          .in('id', sessionIds);

        if (deleteSessionsError) {
          console.error('Error deleting sessions:', deleteSessionsError);
          return { success: false, deletedSessions: 0, deletedNotes: 0, deletedAssessments: 0, deletedWalletTransactions: walletTransactionsCount, error: deleteSessionsError.message };
        }
      }

      // Delete all progress notes
      if (notes.length > 0) {
        const noteIds = notes.map(n => n.id);
        const { error: deleteNotesError } = await supabase
          .from('progress_notes')
          .delete()
          .in('id', noteIds);

        if (deleteNotesError) {
          console.error('Error deleting progress notes:', deleteNotesError);
          return { success: false, deletedSessions: sessionCount, deletedNotes: 0, deletedAssessments: 0, deletedWalletTransactions: walletTransactionsCount, error: deleteNotesError.message };
        }
      }

      // Delete all assessments
      if (assessments.length > 0) {
        const assessmentIds = assessments.map(a => a.id);
        const { error: deleteAssessmentsError } = await supabase
          .from('assessments')
          .delete()
          .in('id', assessmentIds);

        if (deleteAssessmentsError) {
          console.error('Error deleting assessments:', deleteAssessmentsError);
          return { success: false, deletedSessions: sessionCount, deletedNotes: notesCount, deletedAssessments: 0, deletedWalletTransactions: walletTransactionsCount, error: deleteAssessmentsError.message };
        }
      }

      // Delete the episode itself
      const { error: deleteEpisodeError } = await supabase
        .from('episodes')
        .delete()
        .eq('id', episodeId);

      if (deleteEpisodeError) {
        console.error('Error deleting episode:', deleteEpisodeError);
        return { success: false, deletedSessions: sessionCount, deletedNotes: notesCount, deletedAssessments: assessmentsCount, deletedWalletTransactions: walletTransactionsCount, error: deleteEpisodeError.message };
      }

      // Create audit log for this deletion
      await supabase
        .from('audit_logs')
        .insert({
          id: uuidv4(),
          userRole: 'admin',
          action: 'delete',
          resource: 'episode',
          resourceId: episodeId,
          details: {
            deletedSessions: sessionCount,
            deletedNotes: notesCount,
            deletedAssessments: assessmentsCount,
            deletedWalletTransactions: walletTransactionsCount,
            timestamp: new Date().toISOString()
          },
          createdAt: new Date(),
          updatedAt: new Date()
        });

      console.log(`Successfully deleted episode ${episodeId}: ${sessionCount} sessions, ${notesCount} notes, ${assessmentsCount} assessments, ${walletTransactionsCount} wallet transactions`);
      return { success: true, deletedSessions: sessionCount, deletedNotes: notesCount, deletedAssessments: assessmentsCount, deletedWalletTransactions: walletTransactionsCount };

    } catch (error) {
      console.error('Unexpected error in deleteEpisodeWithSessions:', error);
      return {
        success: false,
        deletedSessions: 0,
        deletedNotes: 0,
        deletedAssessments: 0,
        deletedWalletTransactions: 0,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Get computed session dates for an episode (replaces stored fields)
   */
  async getEpisodeSessionDates(
    episodeId: string,
    client?: SupabaseClient
  ): Promise<{
    firstSessionDate: Date | null;
    lastSessionDate: Date | null;
    nextSessionDate: Date | null;
  }> {
    const supabase = client || supabaseClient;
    
    try {
      // Get first completed session
      const { data: firstSession } = await supabase
        .from('sessions')
        .select('start')
        .eq('episodeId', episodeId)
        .eq('status', 'COMPLETED')
        .order('start', { ascending: true })
        .limit(1)
        .single();

      // Get last completed session
      const { data: lastSession } = await supabase
        .from('sessions')
        .select('start')
        .eq('episodeId', episodeId)
        .eq('status', 'COMPLETED')
        .order('start', { ascending: false })
        .limit(1)
        .single();

      // Get next scheduled/confirmed session
      const { data: nextSession } = await supabase
        .from('sessions')
        .select('start')
        .eq('episodeId', episodeId)
        .in('status', ['SCHEDULED', 'CONFIRMED'])
        .gte('start', new Date().toISOString())
        .order('start', { ascending: true })
        .limit(1)
        .single();

      return {
        firstSessionDate: firstSession?.start ? new Date(firstSession.start) : null,
        lastSessionDate: lastSession?.start ? new Date(lastSession.start) : null,
        nextSessionDate: nextSession?.start ? new Date(nextSession.start) : null
      };
    } catch (error) {
      console.error('Error getting episode session dates:', error);
      return {
        firstSessionDate: null,
        lastSessionDate: null,
        nextSessionDate: null
      };
    }
  },

  /**
   * Get count of free sessions used for an episode
   */
  async getFreeSessionsUsed(
    id: string,
    client?: SupabaseClient,
    type: 'episode' | 'group' = 'episode'
  ): Promise<number> {
    const supabase = client || supabaseClient;

    try {
      const query = supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true });

      // Filter by episode or group
      if (type === 'group') {
        query.eq('groupId', id);
      } else {
        query.eq('episodeId', id);
      }

      // Count only free/waived sessions that are scheduled or completed
      // EXCLUDE consultation sessions since they're free by default, not using allocated free sessions
      const { count, error } = await query
        .eq('paymentStatus', 'WAIVED')
        .neq('sessionType', 'CONSULTATION')  // Exclude consultations
        .in('status', ['SCHEDULED', 'CONFIRMED', 'COMPLETED']);

      if (error) {
        console.error(`Error counting free sessions for ${type}:`, error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getFreeSessionsUsed:', error);
      return 0;
    }
  },

  /**
   * Get count of partnership sessions used for an episode
   */
  async getPartnershipSessionsUsed(
    episodeId: string,
    client?: SupabaseClient
  ): Promise<number> {
    const supabase = client || supabaseClient;
    
    try {
      const { count, error } = await supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true })
        .eq('episodeId', episodeId)
        .eq('isPartnershipCovered', true)
        .in('status', ['SCHEDULED', 'CONFIRMED', 'COMPLETED']);

      if (error) {
        console.error('Error counting partnership sessions:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getPartnershipSessionsUsed:', error);
      return 0;
    }
  },

  /**
   * Update episode status (alias for updateStatus for better naming)
   */
  async updateEpisodeStatus(
    episodeId: string,
    status: EpisodeData['status'],
    client?: SupabaseClient
  ): Promise<void> {
    return this.updateStatus(episodeId, status, client);
  },

  /**
   * Merge two episodes by moving all sessions, progress notes, and assessments from source to target
   * WARNING: This is a destructive operation - the source episode will be deleted
   * Should only be used by admin users
   */
  async mergeEpisodes(
    sourceEpisodeId: string,
    targetEpisodeId: string,
    client?: SupabaseClient
  ): Promise<{ success: boolean; movedSessions: number; movedNotes: number; movedAssessments: number; error?: string }> {
    const supabase = client || supabaseClient;

    try {
      // Validate that both episodes exist
      const [sourceEpisode, targetEpisode] = await Promise.all([
        this.getById(sourceEpisodeId, supabase),
        this.getById(targetEpisodeId, supabase)
      ]);

      if (!sourceEpisode) {
        return { success: false, movedSessions: 0, movedNotes: 0, movedAssessments: 0, error: 'Source episode not found' };
      }

      if (!targetEpisode) {
        return { success: false, movedSessions: 0, movedNotes: 0, movedAssessments: 0, error: 'Target episode not found' };
      }

      // Validate that episodes are for the same user
      if (sourceEpisode.userId !== targetEpisode.userId) {
        return {
          success: false,
          movedSessions: 0,
          movedNotes: 0,
          movedAssessments: 0,
          error: 'Cannot merge episodes from different users'
        };
      }

      // Get all sessions, progress notes, and assessments from the source episode
      const [sessionsResult, notesResult, assessmentsResult] = await Promise.all([
        supabase.from('sessions').select('id').eq('episodeId', sourceEpisodeId),
        supabase.from('progress_notes').select('id').eq('episodeId', sourceEpisodeId),
        supabase.from('assessments').select('id').eq('episodeId', sourceEpisodeId)
      ]);

      if (sessionsResult.error) {
        console.error('Error fetching sessions for merge:', sessionsResult.error);
        return { success: false, movedSessions: 0, movedNotes: 0, movedAssessments: 0, error: sessionsResult.error.message };
      }

      const sessions = sessionsResult.data || [];
      const notes = notesResult.data || [];
      const assessments = assessmentsResult.data || [];

      const sessionCount = sessions.length;
      const notesCount = notes.length;
      const assessmentsCount = assessments.length;

      // Move all sessions to the target episode
      if (sessions.length > 0) {
        const sessionIds = sessions.map(s => s.id);
        const { error: updateError } = await supabase
          .from('sessions')
          .update({
            episodeId: targetEpisodeId,
            updatedAt: new Date().toISOString()
          })
          .in('id', sessionIds);

        if (updateError) {
          console.error('Error moving sessions:', updateError);
          return { success: false, movedSessions: 0, movedNotes: 0, movedAssessments: 0, error: updateError.message };
        }
      }

      // Move all progress notes to the target episode
      if (notes.length > 0) {
        const noteIds = notes.map(n => n.id);
        const { error: updateError } = await supabase
          .from('progress_notes')
          .update({
            episodeId: targetEpisodeId,
            updatedAt: new Date().toISOString()
          })
          .in('id', noteIds);

        if (updateError) {
          console.error('Error moving progress notes:', updateError);
          // Rollback sessions
          if (sessions.length > 0) {
            await supabase.from('sessions').update({ episodeId: sourceEpisodeId }).in('id', sessions.map(s => s.id));
          }
          return { success: false, movedSessions: 0, movedNotes: 0, movedAssessments: 0, error: updateError.message };
        }
      }

      // Move all assessments to the target episode
      if (assessments.length > 0) {
        const assessmentIds = assessments.map(a => a.id);
        const { error: updateError } = await supabase
          .from('assessments')
          .update({
            episodeId: targetEpisodeId,
            updatedAt: new Date().toISOString()
          })
          .in('id', assessmentIds);

        if (updateError) {
          console.error('Error moving assessments:', updateError);
          // Rollback sessions and notes
          if (sessions.length > 0) {
            await supabase.from('sessions').update({ episodeId: sourceEpisodeId }).in('id', sessions.map(s => s.id));
          }
          if (notes.length > 0) {
            await supabase.from('progress_notes').update({ episodeId: sourceEpisodeId }).in('id', notes.map(n => n.id));
          }
          return { success: false, movedSessions: 0, movedNotes: 0, movedAssessments: 0, error: updateError.message };
        }
      }

      // Delete the source episode
      const { error: deleteError } = await supabase
        .from('episodes')
        .delete()
        .eq('id', sourceEpisodeId);

      if (deleteError) {
        console.error('Error deleting source episode:', deleteError);
        // Rollback all moves
        if (sessions.length > 0) {
          await supabase.from('sessions').update({ episodeId: sourceEpisodeId }).in('id', sessions.map(s => s.id));
        }
        if (notes.length > 0) {
          await supabase.from('progress_notes').update({ episodeId: sourceEpisodeId }).in('id', notes.map(n => n.id));
        }
        if (assessments.length > 0) {
          await supabase.from('assessments').update({ episodeId: sourceEpisodeId }).in('id', assessments.map(a => a.id));
        }
        return { success: false, movedSessions: 0, movedNotes: 0, movedAssessments: 0, error: deleteError.message };
      }

      // Create audit log for this merge
      await supabase
        .from('audit_logs')
        .insert({
          id: uuidv4(),
          userRole: 'admin',
          action: 'merge',
          resource: 'episode',
          resourceId: targetEpisodeId,
          details: {
            sourceEpisodeId,
            targetEpisodeId,
            movedSessions: sessionCount,
            movedNotes: notesCount,
            movedAssessments: assessmentsCount,
            timestamp: new Date().toISOString()
          },
          createdAt: new Date(),
          updatedAt: new Date()
        });

      console.log(`Successfully merged episode ${sourceEpisodeId} into ${targetEpisodeId}: ${sessionCount} sessions, ${notesCount} notes, ${assessmentsCount} assessments`);
      return { success: true, movedSessions: sessionCount, movedNotes: notesCount, movedAssessments: assessmentsCount };

    } catch (error) {
      console.error('Unexpected error in mergeEpisodes:', error);
      return {
        success: false,
        movedSessions: 0,
        movedNotes: 0,
        movedAssessments: 0,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
};